import { Button, Heading, TextField } from '@invisionag/components';
import { useTranslation } from '@invisionag/next/i18n';
import { useRouter, useSearchParams } from 'next/navigation';
import { Result } from '@invisionag/next/result';
import { CenteredContainer } from '../../_components/centered-container';
import { SetView } from './page';
import { Form, FormMessage, FormResult, SubmitButton } from '@/lib/form';
import * as Cortex from '@/repositories/cortex';
import { getRedirectUrl } from '@/lib/utils';

export function RecoveryCodeForm({ setView }: { setView: SetView }) {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('return_url');

  async function verifyRecoveryCode(formData: FormData): Promise<FormResult> {
    const recoveryCode = assertString(formData.get('recoveryCode'));

    if (!recoveryCode) {
      return Result.Err({
        validationErrors: {
          recoveryCode: t(
            'login.recovery-code-form.field.recoveryCode.validation.valueMissing',
          ),
        },
      });
    }

    const result = await Cortex.verifyRecoveryCode(recoveryCode);

    if (!result.ok) {
      switch (result.err) {
        case 'invalid-recovery-code':
          return Result.Err({
            message: t(`login.recovery-code-form.error.${result.err}`),
          });
        case 'timeout':
          setView('login', {
            message: t(`login.recovery-code-form.error.${result.err}`),
          });
          return Result.Err({});
        default:
          throw new Error(
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `Unexpected error '${result.err satisfies never}'`,
          );
      }
    }

    router.replace(getRedirectUrl(returnUrl));
    return Result.Ok();
  }

  return (
    <CenteredContainer>
      <Heading level={1} className="text-center">
        {t('login.recovery-code-form.heading')}
      </Heading>
      <Form action={verifyRecoveryCode} className="grid gap-4">
        <div className="grid gap-4">
          <TextField
            isRequired
            name="recoveryCode"
            label={t('login.recovery-code-form.field.recoveryCode.label')}
            autoFocus
          />
          <div>
            {t('login.recovery-code-form.field.recoveryCode.recovery-hint')}
          </div>
        </div>
        <FormMessage />
        <SubmitButton className="w-full">
          {t('login.recovery-code-form.submit')}
        </SubmitButton>
        <Button
          variant="link-like"
          onPress={() => setView('login')}
          className="justify-self-center"
        >
          {t('login.recovery-code-form.go-to-login')}
        </Button>
      </Form>
    </CenteredContainer>
  );
}

function assertString(value: unknown): string {
  if (typeof value === 'string') return value;
  throw new Error(`Expected string, got '${typeof value}'`);
}
